if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (!event.detail.currentElement) return;
        this.updateNavigation(event.detail.currentElement);
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      connectedCallback() {
        if (this.hasAttribute('data-mayura-navigation')) {
          this.onNavigationClick = (event) => {
            const button = event.target.closest('[data-gallery-direction]');
            if (!button) return;
            const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
            const current = items.findIndex((item) => item.dataset.mediaId === this.navigationMediaId);
            const next = items[current + (button.dataset.galleryDirection === 'next' ? 1 : -1)];
            if (next) this.setActiveMedia(next.dataset.mediaId, false);
          };
          this.onNavigationSlide = (event) => {
            if (event.detail.currentElement) this.updateNavigation(event.detail.currentElement);
          };
          this.addEventListener('click', this.onNavigationClick);
          this.elements.viewer.addEventListener('slideChanged', this.onNavigationSlide);
          this.setAttribute('data-navigation-ready', '');
          this.updateNavigation(this.elements.viewer.querySelector('.is-active'));
        }
        if (this.closest('product-info[id^="MainProduct-"]') && this.elements.thumbnails) {
          this.onViewerPointerDown = (event) => {
            if (event.pointerType !== 'mouse' || event.button !== 0 || !this.isVerticalGallery()) return;
            const opener = event.target.closest('.product__media-item.is-active .product__modal-opener--image');
            if (!opener || !this.elements.viewer.contains(opener)) return;
            this.viewerDragStart = { x: event.clientX, y: event.clientY, mediaId: opener.closest('[data-media-id]').dataset.mediaId };
          };
          this.onViewerPointerMove = (event) => {
            if (!this.viewerDragStart) return;
            const distanceX = event.clientX - this.viewerDragStart.x;
            const distanceY = event.clientY - this.viewerDragStart.y;
            if (Math.abs(distanceX) < 8 || Math.abs(distanceX) < Math.abs(distanceY) * 1.3) return;
            const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
            const currentIndex = items.findIndex((item) => item.dataset.mediaId === this.viewerDragStart.mediaId);
            const current = items[currentIndex];
            const next = items[currentIndex + (distanceX < 0 ? 1 : -1)];
            if (!current?.querySelector('.product__modal-opener--image')) return;
            this.clearViewerDragPreview();
            const offset = next?.querySelector('.product__modal-opener--image') ? distanceX : distanceX * 0.25;
            current.classList.add('mayura-gallery-dragging');
            current.style.transform = `translateX(${offset}px)`;
            if (next?.querySelector('.product__modal-opener--image')) {
              next.classList.add('mayura-gallery-drag-preview');
              next.style.transform = `translateX(${offset + (distanceX < 0 ? 1 : -1) * current.parentElement.clientWidth}px)`;
            }
          };
          this.onViewerPointerUp = (event) => {
            if (!this.viewerDragStart) return;
            const { x, y, mediaId } = this.viewerDragStart;
            this.viewerDragStart = null;
            const distanceX = event.clientX - x;
            const distanceY = event.clientY - y;
            if (Math.abs(distanceX) < 8 || Math.abs(distanceX) < Math.abs(distanceY) * 1.3) {
              this.clearViewerDragPreview();
              return;
            }
            const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
            const currentIndex = items.findIndex((item) => item.dataset.mediaId === mediaId);
            const next = items[currentIndex + (distanceX < 0 ? 1 : -1)];
            this.suppressViewerClick = true;
            window.setTimeout(() => { this.suppressViewerClick = false; }, 0);
            if (Math.abs(distanceX) >= 55 && next?.querySelector('.product__modal-opener--image')) {
              this.setActiveMedia(next.dataset.mediaId, false);
            } else {
              this.settleViewerDrag();
            }
          };
          this.onViewerClickCapture = (event) => {
            if (!this.suppressViewerClick) return;
            event.preventDefault();
            event.stopImmediatePropagation();
            this.suppressViewerClick = false;
          };
          this.elements.viewer.addEventListener('pointerdown', this.onViewerPointerDown);
          window.addEventListener('pointermove', this.onViewerPointerMove);
          window.addEventListener('pointerup', this.onViewerPointerUp);
          this.onViewerPointerCancel = () => {
            this.viewerDragStart = null;
            this.clearViewerDragPreview();
          };
          window.addEventListener('pointercancel', this.onViewerPointerCancel);
          this.elements.viewer.addEventListener('click', this.onViewerClickCapture, true);
        }
        if (this.dataset.thumbnailPosition !== 'left' || !this.elements.thumbnails) return;
        this.verticalTrack = this.elements.thumbnails.querySelector('[id^="Slider-Thumbnails"]');
        if (!this.verticalTrack) return;
        this.verticalButtons = Array.from(this.querySelectorAll('[data-gallery-vertical-scroll]'));
        this.onVerticalScroll = () => {
          const max = this.verticalTrack.scrollHeight - this.verticalTrack.clientHeight;
          this.verticalButtons.forEach((button) => {
            button.hidden = max <= 1;
            button.disabled = button.dataset.galleryVerticalScroll === 'previous'
              ? this.verticalTrack.scrollTop <= 1
              : this.verticalTrack.scrollTop >= max - 1;
          });
        };
        this.onVerticalClick = (event) => {
          const button = event.target.closest('[data-gallery-vertical-scroll]');
          if (!button || !this.isVerticalGallery()) return;
          this.verticalTrack.scrollBy({
            top: this.verticalTrack.clientHeight * 0.8 * (button.dataset.galleryVerticalScroll === 'previous' ? -1 : 1),
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          });
        };
        this.addEventListener('click', this.onVerticalClick);
        this.verticalTrack.addEventListener('scroll', this.onVerticalScroll, { passive: true });
        this.verticalObserver = new ResizeObserver(this.onVerticalScroll);
        this.verticalObserver.observe(this.verticalTrack);
        this.onVerticalScroll();
      }

      disconnectedCallback() {
        this.clearGalleryTransition();
        this.clearViewerDragPreview();
        this.removeEventListener('click', this.onNavigationClick);
        this.elements.viewer.removeEventListener('slideChanged', this.onNavigationSlide);
        this.removeEventListener('click', this.onVerticalClick);
        this.verticalTrack?.removeEventListener('scroll', this.onVerticalScroll);
        this.verticalObserver?.disconnect();
        this.elements.viewer?.removeEventListener('pointerdown', this.onViewerPointerDown);
        window.removeEventListener('pointermove', this.onViewerPointerMove);
        window.removeEventListener('pointerup', this.onViewerPointerUp);
        window.removeEventListener('pointercancel', this.onViewerPointerCancel);
        this.elements.viewer?.removeEventListener('click', this.onViewerClickCapture, true);
      }

      isVerticalGallery() {
        return this.dataset.thumbnailPosition === 'left' && this.dataset.desktopLayout.includes('thumbnail') && window.matchMedia('(min-width: 990px)').matches;
      }

      updateNavigation(activeMedia) {
        if (!this.hasAttribute('data-mayura-navigation') || !activeMedia) return;
        const items = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        const index = items.indexOf(activeMedia);
        if (index < 0) return;
        this.navigationMediaId = activeMedia.dataset.mediaId;
        const current = this.querySelector('[data-gallery-current]');
        const total = this.querySelector('[data-gallery-total]');
        if (current) current.textContent = index + 1;
        if (total) total.textContent = items.length;
        this.querySelectorAll('[data-gallery-direction]').forEach((button) => {
          button.disabled = button.dataset.galleryDirection === 'previous' ? index === 0 : index === items.length - 1;
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const previousMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
        this.clearGalleryTransition();
        this.clearViewerDragPreview();
        const useFadeTransition = previousMedia && previousMedia !== activeMedia && !prepend &&
          this.closest('product-info[id^="MainProduct-"]') && this.mql.matches &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');
        if (useFadeTransition) {
          previousMedia.classList.add('mayura-gallery-fade-backdrop');
          activeMedia.classList.add('mayura-gallery-fade-in');
          const fade = activeMedia.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: 240,
            easing: 'ease-out',
          });
          this.galleryAnimations = [fade];
          this.galleryOutgoing = previousMedia;
          this.galleryIncoming = activeMedia;
          fade.finished.then(() => {
            if (this.galleryAnimations?.[0] === fade) this.clearGalleryTransition();
          }).catch(() => {});
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.updateNavigation(activeMedia);
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      clearGalleryTransition() {
        this.galleryAnimations?.forEach((animation) => animation.cancel());
        this.galleryAnimations = null;
        this.galleryOutgoing?.classList.remove('mayura-gallery-fade-backdrop');
        this.galleryIncoming?.classList.remove('mayura-gallery-fade-in');
        this.galleryOutgoing = null;
        this.galleryIncoming = null;
      }

      clearViewerDragPreview() {
        this.elements.viewer.querySelectorAll('.mayura-gallery-dragging, .mayura-gallery-drag-preview').forEach((item) => {
          item.classList.remove('mayura-gallery-dragging', 'mayura-gallery-drag-preview');
          item.style.transform = '';
        });
      }

      settleViewerDrag() {
        const current = this.elements.viewer.querySelector('.mayura-gallery-dragging');
        if (!current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.clearViewerDragPreview();
          return;
        }
        const preview = this.elements.viewer.querySelector('.mayura-gallery-drag-preview');
        const timing = { duration: 220, easing: 'cubic-bezier(.2,.72,.2,1)', fill: 'forwards' };
        const animations = [current.animate([
          { transform: current.style.transform },
          { transform: 'translateX(0)' },
        ], timing)];
        if (preview) {
          const direction = preview.style.transform.includes('translateX(-') ? -1 : 1;
          animations.push(preview.animate([
            { transform: preview.style.transform },
            { transform: `translateX(${direction * current.parentElement.clientWidth}px)` },
          ], timing));
        }
        this.galleryAnimations = animations;
        animations[0].finished.then(() => {
          if (this.galleryAnimations?.[0] !== animations[0]) return;
          this.clearGalleryTransition();
          this.clearViewerDragPreview();
        }).catch(() => {});
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.isVerticalGallery() && this.verticalTrack) {
          const item = thumbnail.getBoundingClientRect();
          const track = this.verticalTrack.getBoundingClientRect();
          if (item.top < track.top || item.bottom > track.bottom) {
            this.verticalTrack.scrollBy({ top: item.top - track.top - 4, behavior: 'auto' });
          }
          return;
        }
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia && !this.hasAttribute('data-manual-video')) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
